import api from '@/utils/api';
import registerServer from '@/utils/register-server';
import request, { post } from '@/utils/request';

const {
  login,
  login_sms_challenge,
  first_password_change,
  reauthenticate,
  reauthenticate_sms_challenge,
  security_status,
  phone_challenge,
  phone_verify,
  logout,
  register,
  setting,
  user_info,
  tenant_info,
  factories_list,
  llm_list,
  my_llm,
  set_api_key,
  set_tenant_info,
  add_llm,
  delete_llm,
  deleteFactory,
  model_references,
  test_llm_connection,
  update_llm_api_base,
  set_llm_enabled,
  batch_set_llm_enabled,
  getSystemStatus,
  getEnrichmentStatus,
  getEnrichmentConfig,
  updateEnrichmentConfig,
  controlEnrichment,
  getSystemVersion,
  getSystemTokenList,
  removeSystemToken,
  createSystemToken,
  publishAgent,
  getPublishedAgentStatus,
  revokePublishedAgent,
  getSystemConfig,
  updateSystemWebsiteConfig,
  getSystemSmsConfig,
  updateSystemSmsConfig,
  testSystemSmsConfig,
  setSystemSmsEnabled,
  setLangfuseConfig,
} = api;

const methods = {
  login: {
    url: login,
    method: 'post',
  },
  login_sms_challenge: {
    url: login_sms_challenge,
    method: 'post',
  },
  first_password_change: {
    url: first_password_change,
    method: 'post',
  },
  reauthenticate: {
    url: reauthenticate,
    method: 'post',
  },
  reauthenticate_sms_challenge: {
    url: reauthenticate_sms_challenge,
    method: 'post',
  },
  security_status: {
    url: security_status,
    method: 'get',
  },
  phone_challenge: {
    url: phone_challenge,
    method: 'post',
    suppressNotification: true,
  },
  phone_verify: {
    url: phone_verify,
    method: 'post',
    suppressNotification: true,
  },
  logout: {
    url: logout,
    method: 'get',
  },
  register: {
    url: register,
    method: 'post',
  },
  setting: {
    url: setting,
    method: 'post',
  },
  user_info: {
    url: user_info,
    method: 'get',
  },
  get_tenant_info: {
    url: tenant_info,
    method: 'get',
  },
  set_tenant_info: {
    url: set_tenant_info,
    method: 'post',
  },
  factories_list: {
    url: factories_list,
    method: 'get',
  },
  llm_list: {
    url: llm_list,
    method: 'get',
  },
  my_llm: {
    url: my_llm,
    method: 'get',
  },
  set_api_key: {
    url: set_api_key,
    method: 'post',
  },
  add_llm: {
    url: add_llm,
    method: 'post',
  },
  delete_llm: {
    url: delete_llm,
    method: 'post',
  },
  getSystemStatus: {
    url: getSystemStatus,
    method: 'get',
  },
  getEnrichmentStatus: {
    url: getEnrichmentStatus,
    method: 'get',
  },
  getEnrichmentConfig: {
    url: getEnrichmentConfig,
    method: 'get',
  },
  updateEnrichmentConfig: {
    url: updateEnrichmentConfig,
    method: 'put',
  },
  controlEnrichment: {
    url: controlEnrichment,
    method: 'post',
  },
  getSystemVersion: {
    url: getSystemVersion,
    method: 'get',
  },
  deleteFactory: {
    url: deleteFactory,
    method: 'post',
  },
  model_references: {
    url: model_references,
    method: 'get',
  },
  test_llm_connection: {
    url: test_llm_connection,
    method: 'post',
  },
  update_llm_api_base: {
    url: update_llm_api_base,
    method: 'post',
  },
  set_llm_enabled: {
    url: set_llm_enabled,
    method: 'post',
  },
  batch_set_llm_enabled: {
    url: batch_set_llm_enabled,
    method: 'post',
  },
  listToken: {
    url: getSystemTokenList,
    method: 'get',
  },
  createToken: {
    url: createSystemToken,
    method: 'post',
  },
  publishAgent: {
    url: publishAgent,
    method: 'post',
  },
  getPublishedAgentStatus: {
    url: getPublishedAgentStatus,
    method: 'get',
  },
  revokePublishedAgent: {
    url: revokePublishedAgent,
    method: 'delete',
  },
  removeToken: {
    url: removeSystemToken,
    method: 'delete',
  },
  getSystemConfig: {
    url: getSystemConfig,
    method: 'get',
  },
  updateWebsiteConfig: {
    url: updateSystemWebsiteConfig,
    method: 'put',
  },
  getSmsConfig: {
    url: getSystemSmsConfig,
    method: 'get',
  },
  updateSmsConfig: {
    url: updateSystemSmsConfig,
    method: 'put',
  },
  testSmsConfig: {
    url: testSystemSmsConfig,
    method: 'post',
  },
  setSmsEnabled: {
    url: setSystemSmsEnabled,
    method: 'put',
  },
  setLangfuseConfig: {
    url: setLangfuseConfig,
    method: 'put',
  },
  getLangfuseConfig: {
    url: setLangfuseConfig,
    method: 'get',
  },
  deleteLangfuseConfig: {
    url: setLangfuseConfig,
    method: 'delete',
  },
} as const;

const userService = registerServer<keyof typeof methods>(methods, request);

export const getLoginChannels = () => request.get(api.login_channels);
export const loginWithChannel = (channel: string) =>
  (window.location.href = api.login_channel(channel));
export const loginWithPassword = (payload: Record<string, unknown>) =>
  request.post(login, {
    data: payload,
    suppressNotification: true,
  } as any);
export const requestLoginSmsChallenge = (payload: Record<string, unknown>) =>
  request.post(login_sms_challenge, {
    data: payload,
    suppressNotification: true,
  } as any);

export const listTenantUser = (tenantId: string) =>
  request.get(api.listTenantUser(tenantId));

export const addTenantUser = (tenantId: string, email: string) =>
  post(api.addTenantUser(tenantId), { email });

export const deleteTenantUser = ({
  tenantId,
  userId,
}: {
  tenantId: string;
  userId: string;
}) => request.delete(api.deleteTenantUser(tenantId, userId));

export const listTenant = () => request.get(api.listTenant);

export const agreeTenant = (tenantId: string) =>
  request.put(api.agreeTenant(tenantId));

export default userService;
